<template>
<div class="checkout-page bg-light min-vh-100 py-4 py-md-5">
  <div class="container">
    <!-- Breadcrumb -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb bg-transparent p-0 m-0" style="font-size: 13px;">
        <li class="breadcrumb-item"><router-link :to="{name: 'home'}" class="text-decoration-none text-muted">Inicio</router-link></li>
        <li class="breadcrumb-item"><router-link :to="{name: 'store'}" class="text-decoration-none text-muted">Tienda</router-link></li>
        <li class="breadcrumb-item active text-brand-purple font-weight-bold" aria-current="page">Carrito de Compras</li>
      </ol>
    </nav>

    <!-- Header Banner -->
    <div class="cart-header-banner p-4 p-md-5 rounded-25 text-white mb-4 shadow-sm position-relative overflow-hidden" 
         style="background: linear-gradient(135deg, #7d3c98 0%, #5c2a72 100%);">
      <div class="row align-items-center">
        <div class="col-md-8">
          <h1 class="display-6 font-weight-bold mb-2"><i class="fa fa-shopping-cart me-2"></i> Mi Carrito de Compras</h1>
          <p class="mb-0 opacity-80" style="font-size: 15px;">
            Revisa tus productos seleccionados, finaliza tu compra directa o solicita cotización personalizada para pedidos especiales.
          </p>
        </div>
        <div class="col-md-4 text-md-end mt-3 mt-md-0">
          <div class="d-inline-flex flex-wrap gap-2 justify-content-md-end">
            <span class="badge bg-white text-dark px-3 py-2 rounded-pill shadow-sm" style="font-size: 12px; font-weight: 700;">
              <i class="fa fa-box text-brand-purple me-1"></i> {{ productsInCart.length }} Artículo(s)
            </span>
            <span v-if="itemsConPrecio.length > 0" class="badge bg-success text-white px-3 py-2 rounded-pill shadow-sm" style="font-size: 12px; font-weight: 700;">
              <i class="fa fa-check-circle me-1"></i> {{ itemsConPrecio.length }} Compra Directa
            </span>
            <span v-if="itemsParaCotizar.length > 0" class="badge bg-warning text-dark px-3 py-2 rounded-pill shadow-sm" style="font-size: 12px; font-weight: 700;">
              <i class="fa fa-calculator me-1"></i> {{ itemsParaCotizar.length }} Para Cotizar
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- CARRO VACÍO STATE -->
    <div v-if="productsInCart.length === 0" class="empty-cart-card text-center p-5 bg-white rounded-25 shadow-sm my-4">
      <div class="empty-cart-icon mx-auto mb-4" style="width: 100px; height: 100px; background: #fdfaff; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px dashed #7d3c98;">
        <i class="fa fa-shopping-bag text-brand-purple" style="font-size: 45px;"></i>
      </div>
      <h3 class="font-weight-bold text-dark mb-2">Tu carrito de compras está vacío</h3>
      <p class="text-muted max-w-md mx-auto mb-4" style="font-size: 15px;">
        Aún no has agregado productos o empaques a tu lista. Explora nuestro catálogo y realiza tu pedido fácilmente.
      </p>
      <router-link :to="{name: 'store'}" class="btn btn-brand-purple btn-lg px-5 py-3 rounded-pill font-weight-bold shadow-sm">
        <i class="fa fa-arrow-left me-2"></i> Ir a la Tienda
      </router-link>
    </div>

    <!-- MAIN CART CONTENT -->
    <div v-else class="row g-4">
      <!-- LEFT AREA: ITEMS LIST -->
      <div class="col-lg-8">
        
        <!-- SECCIÓN 1: PRODUCTOS CON PRECIO DEFINIDO (COMPRA DIRECTA) -->
        <div v-if="itemsConPrecio.length > 0" class="cart-section mb-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h5 class="font-weight-bold text-dark mb-0">
              <i class="fa fa-credit-card text-brand-purple me-2"></i> 
              Productos con Precio Definido <span class="badge rounded-pill bg-purple-light text-brand-purple ms-2">{{ itemsConPrecio.length }}</span>
            </h5>
            <span class="text-muted small">Pago en línea directo</span>
          </div>

          <div class="card border-0 shadow-sm rounded-20 overflow-hidden mb-3">
            <div class="list-group list-group-flush">
              <div v-for="item in itemsConPrecio" :key="getItemKey(item)" class="list-group-item p-3 p-md-4">
                <div class="row align-items-center">
                  <!-- Image -->
                  <div class="col-3 col-md-2 text-center mb-3 mb-md-0">
                    <img :src="getItemImage(item)" 
                         @error="handleImgError($event)" 
                         class="img-fluid rounded-15 border main-cart-thumb" 
                         alt="Producto">
                  </div>

                  <!-- Details -->
                  <div class="col-9 col-md-5 mb-3 mb-md-0">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <h6 class="font-weight-bold text-brand-purple mb-0" style="font-size: 16px;">{{ item.nombre || item.name }}</h6>
                      <span class="badge bg-success text-white rounded-pill" style="font-size: 10px;">Precio Fijo</span>
                    </div>
                    <p v-if="item.tamano" class="text-muted small mb-1"><i class="fa fa-ruler-combined me-1"></i> {{ item.tamano }}</p>

                    <!-- Specs breakdown (Grid idéntico) -->
                    <div v-if="hasSpecs(item)" class="item-specs-badge-box p-2 rounded bg-light border mb-1" style="font-size: 12px;">
                      <div class="row g-1">
                        <div class="col-6" v-if="item.papel">
                          <span class="text-muted">Papel:</span> <strong class="text-brand-purple">{{ item.papel }}</strong>
                        </div>
                        <div class="col-6" v-if="item.tinta">
                          <span class="text-muted">Tintas:</span> <strong class="text-dark">{{ item.tinta }}</strong>
                        </div>
                        <div class="col-12" v-if="item.acabados && item.acabados.length > 0 && (Array.isArray(item.acabados) ? item.acabados.length > 0 : item.acabados !== 'Ninguno')">
                          <span class="text-muted">Acabados:</span> <strong class="text-dark">{{ Array.isArray(item.acabados) ? item.acabados.join(', ') : item.acabados }}</strong>
                        </div>
                        <div class="col-12" v-if="item.notas">
                          <span class="text-muted">Anotaciones:</span> <em class="text-dark font-weight-bold">"{{ item.notas }}"</em>
                        </div>
                        <template v-if="item.specs">
                          <template v-for="(val, key) in item.specs">
                            <div class="col-6" :key="key" v-if="!['Papel', 'Tintas', 'Acabados', 'Notas Especiales'].includes(key) && val && val !== 'Ninguno'">
                              <span class="text-muted">{{ key }}:</span> <strong class="text-dark">{{ Array.isArray(val) ? val.join(', ') : val }}</strong>
                            </div>
                          </template>
                        </template>
                      </div>
                    </div>
                  </div>

                  <!-- Quantity Stepper -->
                  <div class="col-6 col-md-3 text-center">
                    <label class="text-muted d-block small mb-1 font-weight-bold">Cantidad</label>
                    <div class="d-inline-flex align-items-center border rounded-pill p-1 shadow-sm" style="background: #ffffff;">
                      <button type="button" class="btn btn-sm btn-circle-qty" @click="disminuirQty(item)">-</button>
                      <input type="number" 
                             class="form-control form-control-sm text-center font-weight-bold mx-1" 
                             style="width: 75px; color: #000000 !important; background-color: #ffffff !important; border: 1px solid #25D366 !important; border-radius: 8px; font-size: 15px; font-weight: 800;" 
                             :value="getItemQty(item)" 
                             @change="updateQty(item, $event.target.value)">
                      <button type="button" class="btn btn-sm btn-circle-qty" @click="aumentarQty(item)">+</button>
                    </div>
                  </div>

                  <!-- Subtotal & Remove -->
                  <div class="col-6 col-md-2 text-end">
                    <span class="text-muted d-block small">Subtotal</span>
                    <span class="h6 font-weight-bold text-brand-green mb-0 d-block">${{ getItemTotal(item).toLocaleString() }} USD</span>
                    <button type="button" class="btn btn-link text-danger p-0 mt-2 d-block w-100 text-end small text-decoration-none font-weight-bold" @click="removeItem(item)" title="Eliminar ítem">
                      <i class="fa fa-trash me-1"></i> Eliminar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SECCIÓN 2: PRODUCTOS PARA COTIZACIÓN VÍA WHATSAPP (SIN PRECIO FIJO) -->
        <div v-if="itemsParaCotizar.length > 0" class="cart-section mb-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h5 class="font-weight-bold text-dark mb-0">
              <i class="fa fa-calculator text-success me-2"></i> 
              Productos para Cotización por WhatsApp <span class="badge rounded-pill bg-success-light text-success ms-2">{{ itemsParaCotizar.length }}</span>
            </h5>
            <span class="text-muted small">Requieren cotización final</span>
          </div>

          <div class="card border-2 border-success shadow-sm rounded-20 overflow-hidden mb-3" style="border-color: #25D366 !important;">
            <div class="card-header bg-success-light border-0 p-3">
              <div class="d-flex align-items-center gap-2 text-dark font-weight-bold" style="font-size: 14px;">
                <i class="fa fa-info-circle text-success" style="font-size: 18px;"></i>
                <span>Los siguientes empaques personalizados requieren revisión de costos de producción. Se enviarán por WhatsApp para cotizar en minutos:</span>
              </div>
            </div>

            <div class="list-group list-group-flush">
              <div v-for="item in itemsParaCotizar" :key="getItemKey(item)" class="list-group-item p-3 p-md-4">
                <div class="row align-items-center">
                  <!-- Image -->
                  <div class="col-3 col-md-2 text-center mb-3 mb-md-0">
                    <img :src="getItemImage(item)" 
                         @error="handleImgError($event)" 
                         class="img-fluid rounded-15 border main-cart-thumb" 
                         alt="Producto">
                  </div>

                  <!-- Details -->
                  <div class="col-9 col-md-5 mb-3 mb-md-0">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <h6 class="font-weight-bold text-dark mb-0" style="font-size: 16px;">{{ item.nombre || item.name }}</h6>
                      <span class="badge bg-warning text-dark rounded-pill" style="font-size: 10px;">Requiere Cotización</span>
                    </div>

                    <!-- Specs breakdown (Grid idéntico) -->
                    <div v-if="hasSpecs(item)" class="item-specs-badge-box p-2 rounded bg-light border mb-1" style="font-size: 12px;">
                      <div class="row g-1">
                        <div class="col-6" v-if="item.papel">
                          <span class="text-muted">Papel:</span> <strong class="text-brand-purple">{{ item.papel }}</strong>
                        </div>
                        <div class="col-6" v-if="item.tinta">
                          <span class="text-muted">Tintas:</span> <strong class="text-dark">{{ item.tinta }}</strong>
                        </div>
                        <div class="col-12" v-if="item.acabados && item.acabados.length > 0 && (Array.isArray(item.acabados) ? item.acabados.length > 0 : item.acabados !== 'Ninguno')">
                          <span class="text-muted">Acabados:</span> <strong class="text-dark">{{ Array.isArray(item.acabados) ? item.acabados.join(', ') : item.acabados }}</strong>
                        </div>
                        <div class="col-12" v-if="item.notas">
                          <span class="text-muted">Anotaciones:</span> <em class="text-dark font-weight-bold">"{{ item.notas }}"</em>
                        </div>
                        <template v-if="item.specs">
                          <template v-for="(val, key) in item.specs">
                            <div class="col-6" :key="key" v-if="!['Papel', 'Tintas', 'Acabados', 'Notas Especiales'].includes(key) && val && val !== 'Ninguno'">
                              <span class="text-muted">{{ key }}:</span> <strong class="text-dark">{{ Array.isArray(val) ? val.join(', ') : val }}</strong>
                            </div>
                          </template>
                        </template>
                      </div>
                    </div>
                  </div>

                  <!-- Quantity Stepper -->
                  <div class="col-6 col-md-3 text-center">
                    <label class="text-muted d-block small mb-1 font-weight-bold">Cantidad</label>
                    <div class="d-inline-flex align-items-center border rounded-pill p-1 shadow-sm" style="background: #ffffff;">
                      <button type="button" class="btn btn-sm btn-circle-qty" @click="disminuirQty(item)">-</button>
                      <input type="number" 
                             class="form-control form-control-sm text-center font-weight-bold mx-1" 
                             style="width: 75px; color: #000000 !important; background-color: #ffffff !important; border: 1px solid #25D366 !important; border-radius: 8px; font-size: 15px; font-weight: 800;" 
                             step="1000"
                             :value="getItemQty(item)" 
                             @change="updateQty(item, $event.target.value)">
                      <button type="button" class="btn btn-sm btn-circle-qty" @click="aumentarQty(item)">+</button>
                    </div>
                  </div>

                  <!-- Price badge & Remove -->
                  <div class="col-6 col-md-2 text-end">
                    <span class="text-muted d-block small">Precio</span>
                    <span class="badge bg-warning text-dark px-2 py-1 rounded mb-1 d-inline-block font-weight-bold">Por Cotizar</span>
                    <button type="button" class="btn btn-link text-danger p-0 mt-2 d-block w-100 text-end small text-decoration-none font-weight-bold" @click="removeItem(item)" title="Eliminar ítem">
                      <i class="fa fa-trash me-1"></i> Eliminar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- WHATSAPP CTA BOX INSIDE QUOTE SECTION -->
            <div class="p-4 bg-light text-center border-top">
              <h6 class="font-weight-bold text-dark mb-2">
                <i class="fa fa-whatsapp text-success me-2" style="font-size: 20px;"></i> ¿Listo para cotizar tus {{ itemsParaCotizar.length }} empaque(s) personalizados?
              </h6>
              <p class="text-muted small mb-3">
                Haz clic en el botón para enviar los detalles de tu pedido directamente a nuestro equipo de producción en WhatsApp.
              </p>
              <a :href="whatsappQuoteLink" target="_blank" class="btn btn-success btn-lg px-4 py-3 rounded-pill font-weight-bold shadow-sm d-inline-flex align-items-center gap-2" style="background-color: #25D366; border: none;">
                <i class="fa fa-whatsapp" style="font-size: 24px;"></i> COTIZAR ESTOS PRODUCTOS POR WHATSAPP
              </a>
            </div>
          </div>
        </div>

      </div>

      <!-- RIGHT SIDEBAR: ORDER SUMMARY & PAYMENT / QUOTE ACTIONS -->
      <div class="col-lg-4">
        <div class="card border-0 shadow-sm rounded-25 sticky-top" style="top: 90px; z-index: 10;">
          <div class="card-body p-4">
            <h5 class="font-weight-bold text-dark mb-4 pb-2 border-bottom">
              <i class="fa fa-calculator text-brand-purple me-2"></i> Resumen del Pedido
            </h5>

            <!-- Summary Rows -->
            <div class="summary-row d-flex justify-content-between mb-2">
              <span class="text-muted">Total Ítems en Carrito:</span>
              <span class="font-weight-bold text-dark">{{ productsInCart.length }}</span>
            </div>

            <div class="summary-row d-flex justify-content-between mb-2" v-if="itemsConPrecio.length > 0">
              <span class="text-muted">Subtotal Compra Directa:</span>
              <span class="font-weight-bold text-dark">${{ totalDirecto.toLocaleString() }} USD</span>
            </div>

            <div class="summary-row d-flex justify-content-between mb-2" v-if="itemsParaCotizar.length > 0">
              <span class="text-muted">Ítems para Cotizar:</span>
              <span class="font-weight-bold text-success">{{ itemsParaCotizar.length }} producto(s)</span>
            </div>

            <div class="summary-row d-flex justify-content-between mb-3" v-if="itemsConPrecio.length > 0">
              <span class="text-muted">Envío / Impuestos:</span>
              <span class="text-muted small font-italic">Calculado en checkout</span>
            </div>

            <div class="total-box p-3 rounded-15 bg-light mb-4 border" v-if="itemsConPrecio.length > 0">
              <div class="d-flex justify-content-between align-items-center">
                <span class="font-weight-bold text-dark h6 mb-0">TOTAL COMPRA DIRECTA:</span>
                <span class="total-amount text-brand-green font-weight-bold h4 mb-0">${{ totalDirecto.toLocaleString() }} USD</span>
              </div>
            </div>

            <!-- ACTION BUTTON 1: FINALIZAR COMPRA DIRECTA -->
            <router-link v-if="itemsConPrecio.length > 0" 
                         :to="{name: 'payment'}" 
                         class="btn btn-brand-purple btn-lg w-100 py-3 rounded-pill font-weight-bold shadow mb-3 text-white d-flex align-items-center justify-content-center gap-2"
                         style="background-color: #7d3c98; border: none; font-size: 16px;">
              <i class="fa fa-credit-card"></i> FINALIZAR COMPRA Y PAGAR
            </router-link>

            <!-- ACTION BUTTON 2: SOLICITAR COTIZACIÓN POR WHATSAPP -->
            <a v-if="itemsParaCotizar.length > 0" 
               :href="whatsappQuoteLink" 
               target="_blank" 
               class="btn btn-success btn-lg w-100 py-3 rounded-pill font-weight-bold shadow mb-3 text-white d-flex align-items-center justify-content-center gap-2" 
               style="background-color: #25D366; border: none; font-size: 15px;">
              <i class="fa fa-whatsapp" style="font-size: 22px;"></i> COTIZAR ESTOS PRODUCTOS POR WHATSAPP
            </a>

            <!-- VACIAR CARRITO BUTTON -->
            <button class="btn btn-sm btn-link text-danger text-decoration-none w-100 mb-3" @click="vaciarCarrito">
              <i class="fa fa-trash me-1"></i> Vaciar Carrito de Compras
            </button>

            <!-- Trust Badges & Payment Icons -->
            <div class="trust-features border-top pt-3 text-muted small">
              <div class="d-flex align-items-center gap-2 mb-2">
                <i class="fa fa-shield text-success" style="font-size: 18px;"></i>
                <span>Garantía de calidad en empaques y acabados.</span>
              </div>
              <div class="d-flex align-items-center gap-2 mb-2">
                <i class="fa fa-truck text-brand-purple" style="font-size: 18px;"></i>
                <span>Envíos seguros a todo el país.</span>
              </div>
              <div class="d-flex align-items-center gap-2">
                <i class="fa fa-lock text-primary" style="font-size: 18px;"></i>
                <span>Pagos directos cifrados y seguros.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

export default {
  name: 'CheckoutView',
  computed: {
    ...mapState(['productsInCart', 'configTienda', 'dominio']),
    itemsConPrecio() {
      return (this.productsInCart || []).filter(item => this.getItemTotal(item) > 0);
    },
    itemsParaCotizar() {
      return (this.productsInCart || []).filter(item => this.getItemTotal(item) === 0);
    },
    totalDirecto() {
      return this.itemsConPrecio.reduce((acc, item) => acc + this.getItemTotal(item), 0);
    },
    numeroWhatsApp() {
      return (this.configTienda && this.configTienda.numero_whatsapp) 
        ? this.configTienda.numero_whatsapp.replace(/[^0-9]/g, '') 
        : '';
    },
    whatsappQuoteLink() {
      let num = this.numeroWhatsApp;
      let msg = `*SOLICITUD DE COTIZACIÓN DE EMPAQUES*\n`;
      msg += `===================================\n`;
      
      this.itemsParaCotizar.forEach((item, index) => {
        let qty = (item.wanted || item.cantidad || 1000).toLocaleString();
        msg += `\n📌 *Item #${index + 1}: ${item.nombre || item.name || 'Empaque'}*\n`;
        msg += `• *Cantidad:* ${qty} unidades\n`;
        if (item.papel) msg += `• *Papel:* ${item.papel}\n`;
        if (item.tinta) msg += `• *Tintas:* ${item.tinta}\n`;
        if (item.acabados && item.acabados.length > 0) {
          msg += `• *Acabados:* ${Array.isArray(item.acabados) ? item.acabados.join(', ') : item.acabados}\n`;
        }
        if (item.notas) msg += `• *Anotaciones Especiales:* "${item.notas}"\n`;
        if (item.specs) {
          Object.keys(item.specs).forEach(k => {
            if (!['Papel', 'Tintas', 'Acabados', 'Notas Especiales'].includes(k) && item.specs[k] && item.specs[k] !== 'Ninguno') {
              msg += `• *${k}:* ${item.specs[k]}\n`;
            }
          });
        }
      });
      
      msg += `\n===================================\n`;
      msg += `*Por favor enviar la cotización de estos empaques.*`;
      
      let text = encodeURIComponent(msg);
      if (num) {
        return `https://wa.me/${num}?text=${text}`;
      }
      return `https://api.whatsapp.com/send?text=${text}`;
    },
    whatsappFullCartLink() {
      let num = this.numeroWhatsApp;
      let msg = `*CONSULTA GENERAL DE CARRITO DE COMPRAS*\n`;
      msg += `===================================\n`;
      
      this.productsInCart.forEach((item, index) => {
        let qty = (item.wanted || item.cantidad || 1).toLocaleString();
        let total = this.getItemTotal(item);
        let totalText = total > 0 ? `$${total.toLocaleString()} USD` : 'Por Cotizar';
        msg += `\n📦 *#${index + 1}: ${item.nombre || item.name || 'Producto'}*\n`;
        msg += `• *Cantidad:* ${qty}\n`;
        msg += `• *Total Estimado:* ${totalText}\n`;
        if (item.papel) msg += `• *Papel:* ${item.papel}\n`;
        if (item.tinta) msg += `• *Tintas:* ${item.tinta}\n`;
        if (item.acabados && item.acabados.length > 0) {
          msg += `• *Acabados:* ${Array.isArray(item.acabados) ? item.acabados.join(', ') : item.acabados}\n`;
        }
        if (item.notas) msg += `• *Notas:* "${item.notas}"\n`;
      });
      
      msg += `\n===================================\n`;
      msg += `*Deseo consultar / confirmar mi pedido con un asesor.*`;
      
      let text = encodeURIComponent(msg);
      if (num) {
        return `https://wa.me/${num}?text=${text}`;
      }
      return `https://api.whatsapp.com/send?text=${text}`;
    }
  },
  methods: {
    ...mapMutations(['removeFromCart', 'changeQuantity']),
    getItemKey(item) {
      return item.cartItemId || item.id || Math.random();
    },
    getItemImage(item) {
      if (item.imagenes && Array.isArray(item.imagenes) && item.imagenes.length > 0) {
        let imgName = item.imagenes[0].nombre || item.imagenes[0];
        return `${this.dominio}/img/productos/${imgName}`;
      }
      if (item.imagen && item.imagen !== 'noimagen') {
        if (item.imagen.startsWith('http')) return item.imagen;
        return `${this.dominio}/img/productos/${item.imagen}`;
      }
      if (item.src) {
        if (item.src.startsWith('http')) return item.src;
        if (item.src.startsWith('/')) return `${this.dominio}${item.src}`;
        return `${this.dominio}/${item.src}`;
      }
      return `${this.dominio}/img/LOGO-LUPA.jpg`;
    },
    handleImgError(event) {
      event.target.src = `${this.dominio}/img/LOGO-LUPA.jpg`;
    },
    getItemQty(item) {
      return item.wanted || item.cantidad || 1;
    },
    getItemPrice(item) {
      if (item.esCotizacion || item.esCotizacion === 'true' || item.papel || item.tinta || item.notas) return 0;
      if (item.valor_unitario !== undefined && item.valor_unitario !== null) return parseFloat(item.valor_unitario);
      if (item.price !== undefined && item.price !== null) return parseFloat(item.price);
      if (item.precio_venta) return parseFloat(item.precio_venta);
      if (item.valor_total && item.wanted) return parseFloat(item.valor_total) / parseFloat(item.wanted);
      return 0;
    },
    getItemTotal(item) {
      if (item.esCotizacion || item.esCotizacion === 'true' || item.papel || item.tinta || item.notas) return 0;
      if (item.valor_total) return parseFloat(item.valor_total);
      let p = this.getItemPrice(item);
      let q = this.getItemQty(item);
      return p * q;
    },
    hasSpecs(item) {
      return item.papel || item.tinta || (item.acabados && item.acabados.length > 0) || item.notas || (item.specs && Object.keys(item.specs).length > 0);
    },
    aumentarQty(item) {
      let current = this.getItemQty(item);
      let step = (item.papel || item.tinta || item.notas) ? 1000 : 1;
      let newQty = current + step;
      this.$store.commit('changeQuantity', { product: item, quantity: newQty });
    },
    disminuirQty(item) {
      let current = this.getItemQty(item);
      let step = (item.papel || item.tinta || item.notas) ? 1000 : 1;
      let newQty = current - step;
      if (newQty < (step === 1000 ? 1000 : 1)) {
        this.removeItem(item);
      } else {
        this.$store.commit('changeQuantity', { product: item, quantity: newQty });
      }
    },
    updateQty(item, rawVal) {
      let val = parseInt(rawVal) || 1;
      if (val < 1) val = 1;
      this.$store.commit('changeQuantity', { product: item, quantity: val });
    },
    removeItem(item) {
      this.$store.commit('removeFromCart', item);
    },
    vaciarCarrito() {
      if (confirm('¿Estás seguro de que deseas vaciar todo el carrito de compras?')) {
        this.$store.commit('purchase');
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap');

.checkout-page {
  font-family: 'Montserrat', sans-serif !important;
}

.text-brand-purple { color: #7d3c98 !important; }
.text-brand-green { color: #6ab04c !important; }
.bg-purple-light { background-color: #f3e5f5 !important; }
.bg-success-light { background-color: #e8f8f0 !important; }

.rounded-15 { border-radius: 15px !important; }
.rounded-20 { border-radius: 20px !important; }
.rounded-25 { border-radius: 25px !important; }

.btn-brand-purple {
  background-color: #7d3c98 !important;
  color: white !important;
  transition: all 0.3s ease;
}
.btn-brand-purple:hover {
  background-color: #6a3381 !important;
  transform: translateY(-2px);
}

.main-cart-thumb {
  width: 70px;
  height: 70px;
  object-fit: cover;
}

.btn-circle-qty {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  background: #ffffff;
  border: 1px solid #25D366;
  color: #25D366;
  font-size: 16px;
  transition: all 0.2s ease;
}
.btn-circle-qty:hover {
  background: #25D366;
  color: white;
}

.sticky-top {
  z-index: 100;
}
</style>